/**
 * Maximum allowed address length for parsing
 * Prevents DoS attacks via extremely long inputs
 */
const MAX_ADDRESS_LENGTH = 2000

/**
 * Regex patterns for address parsing
 * Pre-compiled at module level for performance
 */
const REGEX_PATTERNS = {
  NEWLINE: /[\r\n]{1,100}/g,
  NON_ALPHANUM: /[^a-zA-Z0-9\s\n]/g,
  ZIP: /(\d{5})(?:-?\d{4})?$/,
  CITY_STATE: /^(.{1,100}?)\s+[A-Z]{2}\s*\d{5}/,
  STREET_NUMBER: /^\d+\s+/,
} as const

/**
 * Parsed address components extracted from OCR text
 */
export interface ParsedAddress {
  street: string
  city: string
  zip: string
}

/**
 * Parse OCR-extracted address text into structured components
 *
 * Handles multi-line OCR text that may contain multiple addresses
 * Looks for patterns like: Street, City State ZIP
 *
 * @param fullAddress - Raw OCR text containing address
 * @returns Parsed address components
 */
export const parseOcrAddress = (fullAddress: string): ParsedAddress => {
  // Prevent DoS attacks from extremely long inputs
  if (!fullAddress || fullAddress.length > MAX_ADDRESS_LENGTH) {
    return {street: '', city: '', zip: ''}
  }

  const cleaned = fullAddress
    .replace(REGEX_PATTERNS.NEWLINE, '\n')
    .replace(REGEX_PATTERNS.NON_ALPHANUM, '')

  const lines = cleaned
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)

  if (lines.length === 0) {
    return {street: '', city: '', zip: ''}
  }

  // Find all lines that contain a ZIP code pattern
  const linesWithZip: Array<{index: number; line: string; zip: string}> = []
  lines.forEach((line, index) => {
    const zipMatch = line.match(REGEX_PATTERNS.ZIP)
    if (zipMatch) {
      linesWithZip.push({index, line, zip: zipMatch[1]})
    }
  })

  // If no ZIP found, return empty
  if (linesWithZip.length === 0) {
    return {street: '', city: '', zip: ''}
  }

  // Use the last line with a ZIP (typically the TO address in shipping labels)
  const lastZipLine = linesWithZip[linesWithZip.length - 1]
  const zip = lastZipLine.zip

  // Extract city from the line with ZIP
  const cityMatch = lastZipLine.line.match(REGEX_PATTERNS.CITY_STATE)
  const city = cityMatch ? cityMatch[1].trim() : ''

  // Look for street address in the line immediately before the city/state/zip line
  let street = ''
  if (lastZipLine.index > 0) {
    street = lines[lastZipLine.index - 1]
    // Remove street number from the beginning (e.g., "12345 Main St" -> "Main St")
    street = street.replace(REGEX_PATTERNS.STREET_NUMBER, '')
  }

  return {street, city, zip}
}
